import * as React from "react";
import {
  canComment as accessCanComment,
  canEdit as accessCanEdit,
  type AccessLevel,
} from "#app/lib/utils/access-level";

// Per-issue capability flags derived from the viewer's effective
// access level on the open issue. Provided by the issue body once
// `useIssueViewData` resolves and consumed by edit / note screens
// to gate input. Default values are permissive (true) so the live
// query loading state doesn't briefly disable controls — the
// authoritative backend mutations still block unauthorized writes.
export interface IssueViewCapabilities {
  level: AccessLevel | null;
  canEdit: boolean;
  canComment: boolean;
}

const DEFAULT_CAPS: IssueViewCapabilities = {
  level: null,
  canEdit: true,
  canComment: true,
};

const Ctx = React.createContext<IssueViewCapabilities>(DEFAULT_CAPS);

export function IssueViewCapabilitiesProvider({
  level,
  children,
}: {
  level: AccessLevel | null | undefined;
  children: React.ReactNode;
}) {
  const value = React.useMemo<IssueViewCapabilities>(() => {
    // While the per-issue level is unresolved (initial load), keep
    // the defaults — the backend will reject if it shouldn't have
    // gone through anyway. Once a value comes back we lock down.
    if (level === undefined) return DEFAULT_CAPS;
    const normalized = (level ?? null) as AccessLevel | null;
    return {
      level: normalized,
      canEdit: accessCanEdit(normalized),
      canComment: accessCanComment(normalized),
    };
  }, [level]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useIssueViewCapabilities(): IssueViewCapabilities {
  return React.useContext(Ctx);
}
